import {
  GetInstallationProductDetailDocument,
  GetInstallationProductDetailQuery,
  SaveProduct_Input,
  SaveProductMutation,
  useGetInstallationProductDetailQuery,
  useSaveProductMutation
} from 'generated/graphql';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';


export type IFormState = {
  finish: string;
  hardware: string;
  id: string;
  installationId: string;
  manufacturer: string;
  model: string;
  name: string;
  notes: string;
  supplier: string;
};

const initialState = {
  finish: '',
  hardware: '',
  id: '',
  installationId: '',
  manufacturer: '',
  model: '',
  name: '',
  notes: '',
  supplier: ''
};

export function useFormState(id: string, installationId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      finish: {
        label: 'Finish',
        type: 'text'
      },
      hardware: {
        label: 'Hardware',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      installationId: {
        label: 'Installation id',
        type: 'text'
      },
      manufacturer: {
        label: 'Manufacturer',
        type: 'text'
      },
      model: {
        label: 'Model',
        type: 'text'
      },
      name: {
        label: 'Name',
        type: 'text'
      },
      notes: {
        label: 'Notes',
        type: 'text'
      },
      supplier: {
        label: 'Supplier',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (formState.installationId !== installationId && formState.installationId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'installationId',
          value: installationId
        }
      });
    }
  });


  const { data, loading } = useQuery(installationId);
  useEffect(() => {
    if (!loading && data.id !== formState.id) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState, ...data
        }
      });
    }
  }, [
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export interface IData extends IFormState {}

export function useQuery(installationId: string) {
  const tuple = useGetInstallationProductDetailQuery({
    variables: {
      id: installationId
    },
    skip: installationId === ''
  });

  return React.useMemo(
    () => {
      const productDetails = tuple.data?.installation?.product;
      const data: IData = {
        installationId: tuple.data?.installation?.id ?? '',
        id: productDetails?.id ?? '',
        finish: productDetails?.finish ?? '',
        hardware: productDetails?.hardware ?? '',
        manufacturer: productDetails?.manufacturer ?? '',
        model: productDetails?.model ?? '',
        name: productDetails?.name ?? '',
        notes: productDetails?.notes ?? '',
        supplier: productDetails?.supplier ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}


export function useSaveProductForm() {
  const [
    fnMutate,
    tuple
  ] = useSaveProductMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveProduct_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetInstallationProductDetailQuery, SaveProductMutation>(GetInstallationProductDetailDocument, (prevState, mutationResult) => {
            if (!mutationResult.mutationResult.data?.saveProduct) {
              return prevState;
            }
            return {
              ...prevState,
              installation: {
                ...prevState.installation,
                id: mutationResult.queryVariables.id ?? '',
                __typename: 'Installation',
                product: {
                  ...mutationResult.mutationResult.data.saveProduct
                }

              }
            };
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
